import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { api } from '../apiConfig';
import dashboardStyles from '../pages/Dashboard.module.css';
import styles from './RepresentationManager.module.css';

const RepresentationManager = ({ role }) => {
  const [representations, setRepresentations] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadRepresentations = async () => {
    try {
      const response = await api.get('/galleries/representations');
      setRepresentations(response.data || []);
    } catch (error) {
      console.error('Error loading representations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRepresentations();
  }, []);

  useEffect(() => {
    if (role !== 'gallery' || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }

    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.get(`/users/search?role=artist&q=${encodeURIComponent(query.trim())}`);
        setResults(response.data.users || []);
      } catch (error) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [query, role]);

  const requestRepresentation = async (artist) => {
    try {
      const response = await api.post('/galleries/representations', { artist_id: artist.id });
      if (response.data?.emailSent) {
        toast.success(`Request sent to ${artist.name}`);
      } else {
        toast.success(`Request saved for ${artist.name}. They can respond from their dashboard.`);
      }
      setQuery('');
      setResults([]);
      loadRepresentations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not send the request');
    }
  };

  const respond = async (representation, action) => {
    try {
      const response = await api.post(`/galleries/representations/${representation.id}/${action}`);
      if (action === 'accept') {
        toast.success(response.data?.emailSent
          ? `${representation.gallery.name} was notified that you approved them`
          : `You approved ${representation.gallery.name}`);
      } else {
        toast.success(`You declined ${representation.gallery.name}`);
      }
      loadRepresentations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not update the request');
    }
  };

  const statusLabel = {
    pending: 'Waiting for the artist',
    accepted: 'Approved',
    declined: 'Declined'
  };

  return (
    <section id="representation" className={dashboardStyles.cardSection}>
      <div className={dashboardStyles.sectionHeader}>
        <h2>{role === 'gallery' ? 'Artists you represent' : 'Gallery requests'}</h2>
      </div>
      <p className={styles.intro}>
        {role === 'gallery'
          ? 'Ask an artist for permission to include their published artwork in your exhibitions. Their listing stays theirs. You can upload a new work in their name only after they approve, and only when that title is not already on Art Emerge.'
          : 'A gallery can ask to include your published artwork in their exhibitions. Approving does not give them a second listing, and they cannot change your price or publish for you.'}
      </p>

      {role === 'gallery' && (
        <div className={styles.search}>
          <label htmlFor="artist-representation-search">Find an artist</label>
          <input
            id="artist-representation-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by artist name or email"
          />
          {searching && <p className={styles.meta}>Searching…</p>}
          {results.length > 0 && (
            <ul className={styles.results}>
              {results.map((artist) => (
                <li key={artist.id}>
                  <div>
                    <strong>{artist.name}</strong>
                    {artist.email && <span>{artist.email}</span>}
                  </div>
                  <button type="button" className={styles.primaryButton} onClick={() => requestRepresentation(artist)}>
                    Request approval
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading ? (
        <p className={styles.meta}>Loading requests…</p>
      ) : representations.length === 0 ? (
        <p className={styles.meta}>
          {role === 'gallery' ? 'No representation requests yet.' : 'No galleries have asked to represent you.'}
        </p>
      ) : (
        <ul className={styles.list}>
          {representations.map((representation) => (
            <li key={representation.id}>
              <div>
                <strong>{role === 'gallery' ? representation.artist.name : representation.gallery.name}</strong>
                <span className={`${styles.status} ${styles[representation.status] || ''}`}>
                  {statusLabel[representation.status] || representation.status}
                </span>
              </div>
              {role === 'artist' && representation.status === 'pending' && (
                <div className={styles.actions}>
                  <button type="button" className={styles.secondaryButton} onClick={() => respond(representation, 'decline')}>
                    Decline
                  </button>
                  <button type="button" className={styles.primaryButton} onClick={() => respond(representation, 'accept')}>
                    Approve
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default RepresentationManager;
